import { useEffect, useMemo, useState } from "react";
import {
  CTBProgram,
  NotificationMethods
} from "../../utils/api/endpoints/programs/parsePrograms";
import ToggleRow from "../forms/inputs/ToggleRow";
import usePrivateAccessUsers from "../../utils/hooks/user/usePrivateAccessUsers";
import useAllUsers from "../../utils/hooks/user/useAllUsers";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import { useStoreDispatch } from "../../utils/hooks/hooks";
import { User } from "../../utils/api/endpoints/usersApi";
import useProgram from "../../utils/hooks/programs/useProgram";
import LinkToggle from "../forms/inputs/ExternalLink";
import {
  postSlackLink,
  deleteSlackLink,
  activateMethod
} from "../../utils/api/endpoints/thirdPartyIntegrationsApi";
import { useSaveProgramDetailsMutation, useGetProgramDelegationConfigQuery, useUpdateProgramDelegationConfigMutation, usePauseProgramDelegationMutation, useResumeProgramDelegationMutation } from "../../utils/api/endpoints/programsApi";
import toast from "react-hot-toast";
import EnhancedSearchInput from "../common/EnhancedSearchInput";
import ModernUserCard from "../users/ModernUserCard";
import { CredentialVault } from "../vault";
import { useGetProgramRunsQuery } from "../../utils/api/endpoints/programRunsApi";
import RunScopeVerification from "./RunScopeVerification";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";

const ManageProgram = ({ program }: { program: CTBProgram }) => {
  const { role } = useUserCredentials();
  const isAdminOrBusiness = [
    UserRole.ADMIN,
    UserRole.SUB_ADMIN,
    UserRole.BUSINESS,
    UserRole.BUSINESS_ADMINISTRATOR,
    UserRole.BUSINESS_MANAGER,
    UserRole.DEVELOPER
  ].includes(role as UserRole);

  const dispatch = useStoreDispatch();
  const { users, setFilters } = useAllUsers();
  const { privateAccessUsers, isLoading } = usePrivateAccessUsers(program.id);
  const {
    addPrivateAccessUser,
    removePrivateAccessUser,
    toggleProgramActivation
  } = useProgram(program.id);
  const [saveProgramDetails] = useSaveProgramDetailsMutation();

  const [search, setSearch] = useState("");
  const [captureInput, setCaptureInput] = useState<string>(
    program.slack_channel_link || ""
  );
  const [notificationMethodsPresent, setNotificationMethodsPresent] = useState(
    program.notification_methods || [NotificationMethods.CTB]
  );
  const [details, setDetails] = useState({
    test_lead: program.test_lead || "",
    prepared_by: program.prepared_by || "",
    reviewed_by: program.reviewed_by || "",
    approved_by: program.approved_by || ""
  });

  // Delegation configuration (auto/manual retest assignment)
  const { data: delegationConfig } = useGetProgramDelegationConfigQuery({ programId: program.id });
  const [updateDelegationConfig] = useUpdateProgramDelegationConfigMutation();
  const autoDelegationOn = !!delegationConfig?.auto_delegation_enabled && delegationConfig?.auto_delegation_mode !== "manual_only";
  const [pauseProgramDelegation] = usePauseProgramDelegationMutation();
  const [resumeProgramDelegation] = useResumeProgramDelegationMutation();
  const isPaused = !!delegationConfig?.auto_delegation_paused;


  const handleToggleAutoDelegation = (value: boolean) => {
    updateDelegationConfig({
      programId: program.id,
      data: {
        auto_delegation_enabled: value,
        // When turning on, default to round_robin; when off, force manual_only
        auto_delegation_mode: value ? "round_robin" : "manual_only"
      }
    }).then(res => {
      if ("error" in (res as any)) {
        toast.error("Failed to update auto-delegation setting");
      } else {
        toast.success(`Automatic retest delegation ${value ? "enabled" : "disabled"}`);
      }
    });
  };


  const handlePauseResumeAutomation = async () => {
    if (!autoDelegationOn) return;
    try {
      if (!isPaused) {
        await (pauseProgramDelegation({ programId: program.id }) as any).unwrap();
        toast.success("Retest automation paused");
      } else {
        await (resumeProgramDelegation({ programId: program.id }) as any).unwrap();
        toast.success("Retest automation resumed");
      }
    } catch {
      toast.error("Failed to update automation status");
    }
  };


  useEffect(() => {
    if (program) {
      setDetails({
        test_lead: program.test_lead || "",
        prepared_by: program.prepared_by || "",
        reviewed_by: program.reviewed_by || "",
        approved_by: program.approved_by || ""
      });
    }
  }, [program]);

  const userOptions = useMemo(
    () =>
      users.filter(
        user => user !== undefined && user.role === UserRole.RESEARCHER
      ) as User[],
    [users]
  );

  useEffect(() => {
    setFilters({
      limit: 5,
      roles: [UserRole.RESEARCHER],
      search
    });
  }, [dispatch, search, setFilters]);

  const handleInput = (newValue: string) => {
    setCaptureInput(newValue);
  };

  const handleInsert = async (slackChannelLink: string, programId: string) => {
    postSlackLink(slackChannelLink, programId);
  };

  const handleRemove = async (programId: string) => {
    deleteSlackLink(programId);
  };

  const handleOptIntegrate = async (
    optMethod: NotificationMethods,
    programId: string
  ) => {
    activateMethod(optMethod, programId).then(data =>
      data ? setNotificationMethodsPresent(data) : null
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setDetails(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveDetails = async () => {
    try {
      await saveProgramDetails({ programId: program.id, details });
      toast.success("Program Management Team details updated");
    } catch (error) {
      alert("Failed to save program details.");
      toast.success("Failed to save Program Management Team details");
    }
  };



  return (

    <section className=" h-full space-x-5  space-y-2 bg-white">
      <>
        <div className=" item-center justify-center border-b-[0.5px]  border-blue-500 px-2 py-3 text-[24px] font-medium ">
          Manage
        </div>

        <div className=" flex flex-col gap-8  ">
          <div className="mr-5 mt-4 space-y-3">
            <ToggleRow
              title="Status"
              label={program.isActivated ? "Active" : "Inactive"}
              value={program?.isActivated || false}
              onChange={toggleProgramActivation}
            />

            {/* Auto-Delegation Toggle with Pause/Resume Button */}
            <div className="flex flex-col gap-2">
              <ToggleRow
                title="Auto-Delegation"
                label={autoDelegationOn ? "On" : "Off"}
                value={autoDelegationOn}
                onChange={handleToggleAutoDelegation}
              />
              {autoDelegationOn && (
                <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 rounded-lg border border-gray-200">
                  <button
                    onClick={handlePauseResumeAutomation}
                    className={`rounded-md px-4 py-2 text-sm font-medium text-white transition-all ${isPaused
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-yellow-600 hover:bg-yellow-700"
                      } shadow-sm`}
                  >
                    {isPaused ? "▶ Resume Automation" : "⏸ Pause Automation"}
                  </button>
                  {isPaused && (
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></span>
                      <span className="text-sm font-medium text-yellow-700">Automation paused</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <LinkToggle
              title="Slack webhook url"
              initialValue={captureInput}
              programId={program.id.toString()}
              methodOpted={notificationMethodsPresent.includes(
                NotificationMethods.SLACK
              )}
              optMethod={
                notificationMethodsPresent.includes(NotificationMethods.SLACK)
                  ? undefined
                  : NotificationMethods.SLACK
              }
              onInput={handleInput}
              onInsert={handleInsert}
              onRemove={handleRemove}
              onIntegrate={handleOptIntegrate}
              className="  "
            />
          </div>

          {/* Scope Verification for Scheduled Runs */}
          {isAdminOrBusiness && (
            <div className="mr-5">
              <h2 className="mb-6 text-xl font-medium text-gray-800">
                Scope Verification
              </h2>
              <p className="text-xs text-gray-500 mb-4 px-1">
                Scheduled runs require scope verification 2 days before they start.
              </p>
              <ProgramRunsVerificationList programId={program.id} />
            </div>
          )}

          <div>
            <h2 className="mb-6 text-xl font-medium text-gray-800">
              Assign Users to Private Program
            </h2>

            {/* Modern Search and Add Section */}
            <div className="mb-8 mr-5">
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <h3 className="mb-4 text-lg font-medium text-gray-800">
                  Search & Add Researchers
                </h3>

                <div className="space-y-4">
                  {/* Search Input */}
                  <div className="w-full">
                    <EnhancedSearchInput
                      value={search}
                      onChange={setSearch}
                      placeholder="Search researchers by name or email..."
                      isLoading={isLoading}
                      size="lg"
                      className="w-full"
                    />
                  </div>

                  {/* Search Results Dropdown */}
                  {search && userOptions.length > 0 && (
                    <div className="border border-gray-200 rounded-lg bg-white shadow-lg max-h-80 overflow-y-auto">
                      <div className="p-3">
                        <div className="text-sm font-medium text-gray-700 px-3 py-2 border-b border-gray-100 mb-3">
                          Search Results ({userOptions.length} found)
                        </div>
                        <div className="space-y-2">
                          {userOptions.map((user, index) => (
                            <ModernUserCard
                              key={index}
                              user={user}
                              variant="search-result"
                              clickable
                              onClick={() => {
                                addPrivateAccessUser(user.id);
                                setSearch("");
                              }}
                              className="border-0 shadow-none hover:bg-blue-50"
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* No Results Message */}
                  {search && userOptions.length === 0 && !isLoading && (
                    <div className="text-center py-8 text-gray-500">
                      <div className="mb-2">
                        <svg className="mx-auto h-8 w-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      <p className="text-sm">No researchers found matching "{search}"</p>
                      <p className="text-xs text-gray-400 mt-1">Try adjusting your search terms</p>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-500">
                    💡 Search across usernames, display names, and email addresses. Click on a result to add them to the program.
                  </p>
                </div>
              </div>
            </div>

            {/* Assigned Users Section */}
            <div className="mr-5">
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                <div className="px-6 py-4 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-800">
                      Assigned Researchers
                    </h3>
                    <div className="flex items-center space-x-3">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {privateAccessUsers.length} assigned
                      </span>
                      {privateAccessUsers.length > 0 && (
                        <span className="text-xs text-gray-500">
                          Click 🗑️ to remove
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  {privateAccessUsers.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="text-gray-300 mb-4">
                        <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <h4 className="text-lg font-medium text-gray-900 mb-2">No researchers assigned yet</h4>
                      <p className="text-gray-500 max-w-sm mx-auto">
                        Use the search above to find and assign researchers to this private program. They'll be able to access and work on this program once assigned.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {privateAccessUsers.map((user, index) => (
                        <ModernUserCard
                          key={index}
                          user={user}
                          variant="compact"
                          showActions
                          onAction={(action, userId) => {
                            if (action === 'remove') {
                              removePrivateAccessUser(userId);
                            }
                          }}
                          className="shadow-sm hover:shadow-md border-gray-100"
                        />
                      ))}

                      {/* Loading indicator */}
                      {isLoading && (
                        <div className="flex items-center justify-center py-8">
                          <div className="flex items-center space-x-2 text-gray-500">
                            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span className="text-sm">Loading...</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 rounded-xl bg-white p-8 shadow-md">
            <h2 className="mb-6 text-2xl font-semibold text-gray-800">
              Program Management Team
            </h2>
            <div className="space-y-6">
              {/* First Row */}
              <div className="flex flex-col gap-6 md:flex-row">
                <div className="w-full">
                  <label
                    className="mb-1 block text-sm font-medium text-gray-700"
                    htmlFor="test_lead"
                  >
                    Test Lead
                  </label>
                  <input
                    id="test_lead"
                    type="text"
                    name="test_lead"
                    placeholder="Enter Test Lead Name"
                    value={details.test_lead}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border border-gray-300 p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="w-full">
                  <label
                    className="mb-1 block text-sm font-medium text-gray-700"
                    htmlFor="prepared_by"
                  >
                    Prepared By
                  </label>
                  <input
                    id="prepared_by"
                    type="text"
                    name="prepared_by"
                    placeholder="Enter Name of Preparer"
                    value={details.prepared_by}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border border-gray-300 p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Second Row */}
              <div className="flex flex-col gap-6 md:flex-row">
                <div className="w-full">
                  <label
                    className="mb-1 block text-sm font-medium text-gray-700"
                    htmlFor="reviewed_by"
                  >
                    Reviewed By
                  </label>
                  <input
                    id="reviewed_by"
                    type="text"
                    name="reviewed_by"
                    placeholder="Enter Name of Reviewer"
                    value={details.reviewed_by}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border border-gray-300 p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="w-full">
                  <label
                    className="mb-1 block text-sm font-medium text-gray-700"
                    htmlFor="approved_by"
                  >
                    Approved By
                  </label>
                  <input
                    id="approved_by"
                    type="text"
                    name="approved_by"
                    placeholder="Enter Name of Approver"
                    value={details.approved_by}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border border-gray-300 p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Button */}
              <div className="pt-6">
                <button
                  onClick={handleSaveDetails}
                  className="w-full rounded-lg bg-blue-600 py-3 font-semibold text-white transition-colors duration-200 hover:bg-blue-700"
                >
                  Save Details
                </button>
              </div>
            </div>
          </div>


        </div>
      </>
    </section>
  );
};

const ProgramRunsVerificationList = ({ programId }: { programId: number }) => {
  const { data: runsResponse, refetch } = useGetProgramRunsQuery({ program_id: programId.toString() });
  // The API may return an object like { runs: [...], total: N } or directly an array
  const runs: any[] = Array.isArray(runsResponse)
    ? runsResponse
    : Array.isArray((runsResponse as any)?.runs)
      ? (runsResponse as any).runs
      : Array.isArray((runsResponse as any)?.data)
        ? (runsResponse as any).data
        : [];

  const scheduledRuns = useMemo(() =>
    runs.filter((run: any) => run.status === 'scheduled'),
    [runs]);

  if (scheduledRuns.length === 0) {
    return (
      <div className="p-8 border border-dashed border-slate-200 rounded-2xl text-center text-slate-400 text-xs italic">
        No upcoming scheduled runs requiring verification.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {scheduledRuns.map((run: any) => (
        <RunScopeVerification
          key={run.run_id}
          run={run}
          onActionSuccess={refetch}
        />
      ))}
    </div>
  );
};

export default ManageProgram;
